import { useCallback, useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';
import { parseISO } from 'date-fns';
import type { ChangeEvent } from 'react';
import type { ResultOf } from '@graphql-typed-document-node/core';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { DatePicker } from '@mui/x-date-pickers';

import { isDefined } from '@e-socna/utils';
import {
  getFragmentData,
  graphql,
  ThirdPartyTypeEnum,
} from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { getTouchedError } from '../../../utils/get-touched-error.util';
import { useThirdPartiesQuery } from '../../../components/ThirdParties/hooks/useThirdPartiesQuery.hook';
import { ThirdPartiesCmp_Person_Fragment } from '../../../components/ThirdParties/fragments/person.fragment';
import { sortedByThirdPartyName } from '../../../components/ThirdParties/utils/by-third-party-name.util';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { MissionCategoryFormField } from '../../../components/MissionCategoryAutocomplete/MissionCategoryFormField';
import { IdSelect } from '../../../components/IdSelect';
import type { IdOption } from '../../../components/IdSelect';

import { FolderBusinessEntityType } from '../enums/folder-business-entity-type.enum';
import { InfoAlert } from '../../../components/InfoAlert';

export const FolderBusinessEntityForm_GeneralInfoStep_FolderBusinessEntity_Fragment =
  graphql(`
    fragment FolderBusinessEntityForm_GeneralInfoStep_FolderBusinessEntity_Fragment on FolderBusinessEntity {
      internalMemo
      memo
      color
      businessCreatorId
      businessCreationDate
      workflowId
      isConfidential
    }
  `);

export type FolderBusinessEntityForm_GeneralInfoStep_FolderBusinessEntity =
  ResultOf<
    typeof FolderBusinessEntityForm_GeneralInfoStep_FolderBusinessEntity_Fragment
  >;

export const FolderBusinessEntityForm_GeneralInfoStep_Describable_Fragment =
  graphql(`
    fragment FolderBusinessEntityForm_GeneralInfoStep_Describable_Fragment on Describable {
      title
      description
    }
  `);

export type FolderBusinessEntityForm_GeneralInfoStep_Describable = ResultOf<
  typeof FolderBusinessEntityForm_GeneralInfoStep_Describable_Fragment
>;

export const FolderBusinessEntityForm_GeneralInfoStep_OptionallyDescribable_Fragment =
  graphql(`
    fragment FolderBusinessEntityForm_GeneralInfoStep_OptionallyDescribable_Fragment on OptionallyDescribable {
      title
      description
    }
  `);

export type FolderBusinessEntityForm_GeneralInfoStep_OptionallyDescribable =
  ResultOf<
    typeof FolderBusinessEntityForm_GeneralInfoStep_OptionallyDescribable_Fragment
  >;

interface Props {
  readonly fallbackDescribable?: FragmentType<
    typeof FolderBusinessEntityForm_GeneralInfoStep_Describable_Fragment
  >;
  readonly id: string;
  readonly type: FolderBusinessEntityType;
  readonly workflows: readonly Readonly<{
    _id: string;
    label: string;
  }>[];
}

export const FolderBusinessEntityGeneralInfoFormStep = ({
  fallbackDescribable: pFallbackDescribable,
  type,
  workflows,
}: Readonly<Props>) => {
  const {
    errors,
    handleBlur,
    handleChange,
    setFieldTouched,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<
    FolderBusinessEntityForm_GeneralInfoStep_FolderBusinessEntity &
      (
        | FolderBusinessEntityForm_GeneralInfoStep_Describable
        | FolderBusinessEntityForm_GeneralInfoStep_OptionallyDescribable
      )
  >();

  const { authenticationData } = useAuthenticationContext();

  const { data: internalPeopleQuery, isSuccess: internalPeopleQueryIsSuccess } =
    useThirdPartiesQuery({
      isInternal: true,
      kind: ThirdPartyTypeEnum.Person,
    });

  const internalPeopleLov =
    !internalPeopleQueryIsSuccess ||
    !internalPeopleQuery ||
    !('findSomePeople' in internalPeopleQuery)
      ? [
          {
            key: values.businessCreatorId,
            value: 'Chargement...',
          },
        ]
      : sortedByThirdPartyName(
          getFragmentData(
            ThirdPartiesCmp_Person_Fragment,
            internalPeopleQuery.findSomePeople,
          ),
        ).map((person) => ({
          key: person._id,
          value: `${person.firstName ? `${person.firstName} ` : EMPTY_STRING}${person.lastName}`,
        }));

  const switchAutoColor = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (!checked) {
        void setFieldValue('color', '#ffffff');
      }
    },
    [setFieldValue],
  );

  const clearColor = useCallback(
    () => void setFieldValue('color', undefined),
    [setFieldValue],
  );

  const handleBusinessCreationDateChange = useCallback(
    (date: unknown) => void setFieldValue('businessCreationDate', date),
    [setFieldValue],
  );

  const handleBusinessCreationDateBlur = useCallback(
    () => void setFieldTouched('businessCreationDate', true),
    [setFieldTouched],
  );

  const handleWorkflowIdChange = useCallback(
    (id: Id | typeof EMPTY_STRING) => {
      void setFieldValue('workflowId', id);
    },
    [setFieldValue],
  );

  const handleBusinessCreatorIdChange = useCallback(
    (id: Id | typeof EMPTY_STRING) => {
      void setFieldValue('businessCreatorId', id);
    },
    [setFieldValue],
  );

  const businessCreationDateValue = useMemo(
    () =>
      typeof values.businessCreationDate === 'string'
        ? parseISO(values.businessCreationDate)
        : values.businessCreationDate,
    [values.businessCreationDate],
  );

  const fallbackDescribable = getFragmentData(
    FolderBusinessEntityForm_GeneralInfoStep_Describable_Fragment,
    pFallbackDescribable,
  );
  const fallbackTitle = fallbackDescribable?.title;
  const fallbackDescription = fallbackDescribable?.description;
  const formTitle = values.title;
  const formDescription = values.description;
  const canInherit = !!fallbackDescribable;
  const mustInherit =
    canInherit && !isDefined(formTitle) && !isDefined(formDescription);

  const customize = useCallback(() => {
    void setFieldValue('title', fallbackTitle);
    void setFieldValue('description', fallbackDescription);
  }, [setFieldValue, fallbackTitle, fallbackDescription]);

  const reinitialize = useCallback(() => {
    void setFieldValue('title', undefined);
    void setFieldValue('description', undefined);
  }, [setFieldValue]);

  return (
    <Grid container spacing={2}>
      {canInherit ? (
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <InfoAlert
            action={
              <>
                <Button
                  color="inherit"
                  onClick={mustInherit ? customize : reinitialize}
                  size="small"
                >
                  {mustInherit ? 'Spécifier' : 'Réinitialiser'}
                </Button>
              </>
            }
            sx={{ display: 'flex', flex: 1, flexGrow: 1, mt: 1 }}
          >
            {mustInherit
              ? 'Le titre ainsi que la description sont pour le moment hérités.'
              : 'Le titre ainsi que la description peuvent être hérités.'}
          </InfoAlert>
        </Grid>
      ) : null}
      {type === FolderBusinessEntityType.Mission && (
        <MissionCategoryFormField required />
      )}
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          disabled={mustInherit}
          error={!!(errors.title && touched.title)}
          fullWidth
          helperText={getTouchedError(errors, touched, 'title')}
          id="folder-business-entity-general-info-title"
          label="Titre"
          name="title"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={mustInherit ? fallbackTitle : formTitle}
          variant="outlined"
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          disabled={mustInherit}
          error={
            !!(getIn(errors, 'description') && getIn(touched, 'description'))
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'description')}
          id="folder-business-entity-general-info-description"
          label="Description"
          multiline
          name="description"
          onBlur={handleBlur}
          onChange={handleChange}
          rows={4}
          type="text"
          value={mustInherit ? fallbackDescription : formDescription}
          variant="outlined"
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdSelect<Id, IdOption<Id>>
          error={!!(errors.workflowId && touched.workflowId)}
          helperText={
            !!(errors.workflowId && touched.workflowId) &&
            (getIn(errors, 'workflowId') as string)
          }
          id="folder-business-entity-general-info-workflow-id"
          label="Workflow"
          name="workflowId"
          onBlur={handleBlur}
          onChange={handleWorkflowIdChange}
          options={workflows.map((workflow) => ({
            id: workflow._id as Id,
            label: workflow.label,
          }))}
          required
          value={values.workflowId}
        />
      </Grid>
      <Grid
        size={{
          lg: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(getIn(errors, 'internalMemo') && getIn(touched, 'internalMemo'))
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'internalMemo')}
          id="folder-business-entity-general-info-internal-memo"
          label="Mémo interne"
          multiline
          name="internalMemo"
          onBlur={handleBlur}
          onChange={handleChange}
          rows={4}
          type="text"
          value={values.internalMemo}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          lg: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={!!(getIn(errors, 'memo') && getIn(touched, 'memo'))}
          fullWidth
          helperText={getTouchedError(errors, touched, 'memo')}
          id="folder-business-entity-general-info-memo"
          label="Mémo"
          multiline
          name="memo"
          onBlur={handleBlur}
          onChange={handleChange}
          rows={4}
          type="text"
          value={values.memo}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <DatePicker
          displayWeekNumber
          label="Date de création"
          onChange={handleBusinessCreationDateChange}
          showDaysOutsideCurrentMonth
          slotProps={{
            field: {
              clearable: true,
            },
            actionBar: {
              actions: ['cancel', 'clear', 'today'],
            },
            textField: {
              error: !!getIn(errors, 'businessCreationDate'),
              fullWidth: true,
              helperText: getIn(errors, 'businessCreationDate') as string,
              id: 'folder-business-entity-general-info-business-creation-date',
              name: 'businessCreationDate',
              onBlur: handleBusinessCreationDateBlur,
              required: true,
            },
          }}
          value={businessCreationDateValue}
          views={['year', 'month', 'day']}
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1, display: 'flex', alignItems: 'center' }}
      >
        {!values.color ? (
          <FormControl error={!!(errors.color && touched.color)} fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={!values.color}
                  id="folder-business-entity-general-info-auto-color"
                  name="color"
                  onBlur={handleBlur}
                  onChange={switchAutoColor}
                />
              }
              label="Couleur automatique"
            />
            {!!(errors.color && touched.color) && (
              <FormHelperText>{errors.color}</FormHelperText>
            )}
          </FormControl>
        ) : (
          <>
            <TextField
              error={!!(getIn(errors, 'color') && getIn(touched, 'color'))}
              helperText={getTouchedError(errors, touched, 'color')}
              id="folder-business-entity-general-info-color"
              label="Couleur"
              name="color"
              onBlur={handleBlur}
              onChange={handleChange}
              sx={{ flexGrow: 1 }}
              type="color"
              value={values.color}
              variant="outlined"
            />
            <Box sx={{ ml: 1 }}>
              <Tooltip title="Générer la couleur automatiquement">
                <IconButton
                  aria-label="Générer la couleur automatiquement"
                  onClick={clearColor}
                >
                  <AutoAwesomeIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <IdSelect<Id, IdOption<Id>>
          disabled
          error={!!(errors.businessCreatorId && touched.businessCreatorId)}
          helperText={
            !!(errors.businessCreatorId && touched.businessCreatorId) &&
            (getIn(errors, 'businessCreatorId') as string)
          }
          id="folder-business-entity-general-info-business-creator-id"
          label="Créateur"
          name="businessCreatorId"
          onBlur={handleBlur}
          onChange={handleBusinessCreatorIdChange}
          options={internalPeopleLov.map((person) => ({
            id: person.key,
            label: person.value,
          }))}
          required
          value={values.businessCreatorId}
        />
      </Grid>
      {/* A collaboration is never confidential. */}
      {type !== FolderBusinessEntityType.Collaboration && (
        <Grid
          size={{
            md: 6,
            xs: 12,
          }}
          sx={{ pt: 1, pb: 1, display: 'flex', alignItems: 'center' }}
        >
          <FormControl
            error={!!(errors.isConfidential && touched.isConfidential)}
            fullWidth
          >
            <FormControlLabel
              control={
                <Switch
                  checked={values.isConfidential}
                  disabled={
                    authenticationData?.person._id !== values.businessCreatorId
                  }
                  id="folder-business-entity-general-info-is-confidential"
                  name="isConfidential"
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              }
              label="Confidentiel ?"
            />
            {!!(errors.isConfidential && touched.isConfidential) && (
              <FormHelperText>{errors.isConfidential}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};
